import * as React from 'react';
import { useRef, useEffect, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import { Link } from 'gatsby';
import clsx from 'clsx';
import {
  NODE_PARAGRAPH,
  render,
  MARK_LINK,
} from 'storyblok-rich-text-react-renderer';
import FancyTitle from '../fancy-title';
import { elementIsVisibleInViewport } from '../../lib/utils';
import SplitPane from '../split-pane';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/virtual';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
import useWindowSize from '../../hooks/useWindowSize';
import LinkResolver from '../../helpers/link-resolver';
import { getStoryblokAlt } from '../../helpers/common';

const getStoryblokDimensions = (url) => {
  const [rawWidth, rawHeight] = url?.split('/')[5]?.split('x') || [];
  const width = Number.parseInt(rawWidth, 10);
  const height = Number.parseInt(rawHeight, 10);

  return {
    width: Number.isNaN(width) ? undefined : width,
    height: Number.isNaN(height) ? undefined : height,
  };
};

function VerticalSlider({
  items,
  background,
  itemHeadline = false,
  subheadlineMobile = false,
  blok,
}) {
  const ref = useRef();
  let isSubtitle = false;
  let isHeadline = false;

  if (blok.slides.some((e) => e.Subtitle && e.Subtitle.trim() !== '')) {
    isSubtitle = true;
  }

  if (blok.slides.every((e) => e.Title !== '')) {
    isHeadline = true;
  }

  const windowSize = useWindowSize();

  const shouldShowFixedTitle = useMemo(() => {
    if (isSubtitle) {
      return false;
    }

    if (!blok || !blok.slides || blok.slides.length === 0) {
      return false;
    }

    // Title check - treat empty strings and undefined the same way
    const hasValidTitle = (slide) => {
      if (!slide || !slide.Title) return false;
      if (typeof slide.Title !== 'string') return false;
      return slide.Title.trim().length > 0;
    };

    // Check if the first slide has a valid title
    const firstHasTitle = hasValidTitle(blok.slides[0]);

    // Check if all other slides have no valid titles
    const restHaveNoTitle = blok.slides.slice(1).every((slide) => !hasValidTitle(slide));

    return firstHasTitle && restHaveNoTitle;
  }, [blok.slides, isSubtitle]); // TODO: need to Optimize this useMemo to avoid unnecessary recalculations

  const getHeight = (element) => {
    const subtitleMobileHeight = document.querySelectorAll(element);
    let maxHeight = 0;

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      subtitleMobileHeight[i].style = 'height: auto';
    }

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      if (subtitleMobileHeight[i].offsetHeight > maxHeight) {
        maxHeight = subtitleMobileHeight[i].offsetHeight;
      }
    }

    for (let i = 0; i < subtitleMobileHeight.length; i++) {
      subtitleMobileHeight[i].style = `height: ${maxHeight}px`;
    }
  };

  useEffect(() => {
    window.lastScrollY = 0;
    let animationFrameId = null;

    const handleImageVisibility = () => {
      if (window.innerWidth > 1024) {
        if (ref.current) {
          const allImages = [...ref.current.querySelectorAll('[data-image]')];
          const refY = ref.current.getBoundingClientRect().y;

          if (refY < 0) {
            ref.current
              .querySelectorAll('[data-text]')
              .forEach((item, index) => {
                if (index >= allImages.length - 1) return;
                const image = ref.current.querySelector(
                  `[data-image="${item.dataset.text}"]`,
                );
                if (elementIsVisibleInViewport(item, true)) {
                  image?.classList.add('opacity-100');
                  image?.classList.remove('opacity-0');
                } else {
                  image?.classList.add('opacity-0');
                  image?.classList.remove('opacity-100');
                }
              });
          }
        }
      }
      window.lastScrollY = window.scrollY;
    };

    const onScroll = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        handleImageVisibility();
      });
    };

    handleImageVisibility();
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Added new useEffect for height calculation stabilization
  useEffect(() => {
    const timer = setTimeout(() => {
      if (windowSize.width < 1025) {
        getHeight('.subtitleMobile .fancy-title');
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [windowSize.width, blok.slides]);

  const ALLOWED_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
  const resolveTag = (slide) => {
    const requested = String(
      slide?.headline_level || blok?.headline_level || '',
    ).toLowerCase();
    return ALLOWED_TAGS.includes(requested) ? requested : 'h2';
  };

  return (
    <div
      className="flex flex-wrap lg:flex-nowrap h-full top-0 items-start lg:grid grid-cols-[1.5fr_1fr] auto-rows-fr lg:-ml-16  w-screen lg:w-[calc(100%+4rem)]"
      ref={ref}
    >
      {(windowSize.width < 1025) ? (
        <>
          {shouldShowFixedTitle && (
            <div className="px-4 start subtitleMobile w-full">
              <FancyTitle
                text={blok.slides[0].Title}
                tag={resolveTag(blok.slides[0])}
                marginBottom="mb-12 lg:mb-6"
                textAlign="text-left"
              />
            </div>
          )}

          <Swiper
            modules={[Pagination]}
            direction="horizontal"
            spaceBetween={0}
            slidesPerView={1}
            className="w-screen h-full"
            pagination={{ clickable: true }}
            onBeforeInit={() => getHeight('.subtitleMobile .fancy-title')}
            onResize={() => getHeight('.subtitleMobile .fancy-title')}
            autoHeight
          >
            {blok
                && blok.slides.map((item, index) => (
                  <SwiperSlide
                    className={clsx(blok.slideslength > 1 ? 'cursor-grab' : '')}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${item.id}-${item.title}-${index}`}
                    id={item.id ? item.id : null}
                  >
                    <SplitPane
                      className="h-full"
                      left={(
                        <div className="flex flex-col w-full">
                          {(item.Title || item.Subtitle) && !shouldShowFixedTitle && (
                            <div className="px-4 start subtitleMobile">
                              <FancyTitle
                                text={item.Title || ''}
                                subtitle={item.Subtitle || ''}
                                tag={resolveTag(item)}
                                marginBottom="mb-12 lg:mb-6"
                                textAlign="text-left"
                              />
                            </div>
                          )}
                          <div
                            style={{
                              background:
                                background
                                || item.background_color
                                || item.background
                                || 'transparent',
                            }}
                          >
                            <img
                              data-swiper-parallax-y={400}
                              src={item.image.filename}
                              alt={getStoryblokAlt(item.image)}
                              width={getStoryblokDimensions(item.image.filename).width}
                              height={getStoryblokDimensions(item.image.filename).height}
                              className="w-full h-[50vh] lg:max-h-[50vh] xl:max-h-[60vh] object-contain"
                              loading={index === 0 ? 'eager' : 'lazy'}
                              decoding="async"
                              // backgroundColor={
                              //   background || item.background_color || 'transparent'
                              // }
                            />
                          </div>
                        </div>
                      )}
                      right={(
                        <div className="flex flex-col justify-end h-full p-4 pb-8 xll:p-24 xll:pb-24">
                          <p
                            data-swiper-parallax-opacity={0}
                            data-swiper-parallax={-200}
                            className={clsx(item?.link?.url ? '' : 'mb-8')}
                          >
                            {render(item.description, {
                              markResolvers: {
                                [MARK_LINK]: LinkResolver,
                              },
                              nodeResolvers: {
                                // eslint-disable-next-line react/no-unstable-nested-components
                                [NODE_PARAGRAPH]: (children) => (
                                  <p>{children}</p>
                                ),
                              },
                            })}
                          </p>
                          {(item?.cta_link?.url || item?.cta_link?.story) && (
                          <Link
                            to={item?.cta_link?.url ? item?.cta_link?.url : `/${item.cta_link.story.full_slug}`}
                            title={item?.cta_link?.link_title || undefined}
                            className="inline-block mt0 lg:mt-4 mb-8 button-primary max-w-max"
                          >
                            {item.cta_text}
                          </Link>
                          )}
                        </div>
                      )}
                      rightWidth="xll:w-2/5"
                      leftWidth="xll:w-3/5"
                    />
                  </SwiperSlide>
                ))}
          </Swiper>
        </>
      ) : (
        <>
          <div className="sticky top-0 grid w-full h-screen col-start-1 place-content-center justify-left">
            {blok.slides
              && blok.slides
                .slice(0)
                .reverse()
                .map((item, index, array) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div
                    key={`image-${item._uid}`}
                    className="transparent_background"
                  >
                    {item.image && (
                      <div
                        className={`${
                          [
                            array[index + 1]?.image?.name,
                            array[index - 1]?.image?.name,
                          ].includes(item.image.name)
                            ? ''
                            : 'transition-opacity duration-[300ms]'
                        } absolute inset-0`}
                        data-image={array.length - index}
                        style={{
                          background:
                            background
                            || item.background_color
                            || item.background
                            || 'transparent',
                        }}
                      >
                        <img
                          src={item?.image?.filename}
                          alt={getStoryblokAlt(item?.image)}
                          width={getStoryblokDimensions(item?.image?.filename).width}
                          height={getStoryblokDimensions(item?.image?.filename).height}
                          className="w-full h-full object-contain"
                          loading={index === array.length - 1 ? 'eager' : 'lazy'}
                          decoding="async"
                          // backgroundColor={
                          //   background
                          //   || item.background_color
                          //   || item.background
                          //   || 'transparent'
                          // }
                        />
                      </div>
                    )}
                  </div>
                ))}
          </div>

          {blok
            && blok.slides.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div
                className="flex flex-col justify-center h-full col-start-2 p-4 pb-8 lg:p-8 xl:p-16 lg:pb-24 "
                key={`text-${item._uid}`}
                id={item.id ? item.id : null}
              >
                {item.Title || item.subtitle ? (
                  <div data-text={index + 1}>
                    <FancyTitle
                      text={item.Title}
                      subtitle={item.subtitle}
                      tag={resolveTag(item)}
                    />
                  </div>
                ) : (
                  <div data-text={index + 1} />
                )}

                {render(item.description, {
                  markResolvers: {
                    [MARK_LINK]: LinkResolver,
                  },
                  nodeResolvers: {
                    // eslint-disable-next-line react/no-unstable-nested-components
                    [NODE_PARAGRAPH]: (children) => (
                      <p className>{children}</p>
                    ),
                  },
                })}

                {(item?.cta_link?.url || item?.cta_link?.story) && (
                <Link
                  to={item?.cta_link?.url ? item?.cta_link?.url : `/${item.cta_link.story.full_slug}`}
                  title={item?.cta_link?.link_title || undefined}
                  className="inline-block mt-0 lg:mt-7 mb-8 button-primary max-w-max"
                >
                  {item.cta_text}
                </Link>
                )}
              </div>
            ))}
        </>
      )}
    </div>
  );
}

export default VerticalSlider;
